import { CatalogRendererProps } from '@copilotkit/a2ui-renderer';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ChevronIcon } from '~/assets';
import Checkbox from '~/components/Checkbox';
import Dropdown from '~/components/Dropdown';
import Input from '~/components/Input';
import { CustomFieldType } from '~/types/api.graphql';
import {
  buildKeptColumnsValue,
  CustomerListColumn,
  customerListColumnsSchema,
  customerListKeptColumnsStartSchema,
  customerListKeptColumnsValueSchema,
  defaultKeptColumnDraft,
  findKeptColumnsIssues,
  KeptColumnDraft,
  parseJsonProp,
  readKeptColumnsValue,
} from '~/utils/customerListMatchingRules';

import UnavailableFormField from '../UnavailableFormField';
import { ErrorLine, FallbackCard } from '../visitiqCatalog.styles';
import {
  Chevron,
  ColumnCell,
  Description,
  GridRow,
  HeaderCell,
  KeptColumnsList,
  Muted,
  RowError,
  Section,
  SectionTitle,
  SectionToggle,
  SuggestedHint,
} from './CustomFieldsField.styles';
import KeptColumnLine from './KeptColumnLine';
import KeptColumnsDisplay from './KeptColumnsDisplay';

const FIELD_TYPES = Object.values(CustomFieldType);
// Kept columns listed in the collapsed header before "+N more"
const MAX_LISTED_KEPT_COLUMNS = 3;

interface CustomFieldsRendererProps {
  columns?: string;
  attachmentId?: string;
  label?: string;
  displayOnly?: boolean;
  value?: string;
  setValue: (value: string) => void;
}

/**
 * `VIQCustomFields`: the mapping form's collapsed **File columns to keep** section, or, with `displayOnly`, the submitted
 * kept columns read-only on the confirmation card.
 */
const VisitiqCustomFields = ({ props }: CatalogRendererProps<CustomFieldsRendererProps>): JSX.Element => {
  const { t } = useTranslation();

  if (props.displayOnly) {
    const value = parseJsonProp(customerListKeptColumnsValueSchema, props.value);
    return value ? (
      <KeptColumnsDisplay label={props.label} value={value} />
    ) : (
      <FallbackCard>{t('aiInsights.a2ui.unavailable')}</FallbackCard>
    );
  }

  const columns = parseJsonProp(customerListColumnsSchema, props.columns);
  if (!columns?.length || !props.attachmentId) {
    return <UnavailableFormField message={t('aiInsights.a2ui.unavailable')} setValue={props.setValue} />;
  }
  return (
    // A new file starts a new form, as the mapping form does
    <KeptColumnsEditor
      key={props.attachmentId}
      attachmentId={props.attachmentId}
      columns={columns}
      value={props.value}
      setValue={props.setValue}
    />
  );
};

interface KeptColumnsEditorProps {
  attachmentId: string;
  columns: CustomerListColumn[];
  value?: string;
  setValue: (value: string) => void;
}

const KeptColumnsEditor = ({ attachmentId, columns, value, setValue }: KeptColumnsEditorProps): JSX.Element => {
  const { t } = useTranslation();
  const [drafts, setDrafts] = useState<Record<number, KeptColumnDraft>>(() => {
    const earlierValue = parseJsonProp(customerListKeptColumnsStartSchema, value);
    return earlierValue?.attachmentId === attachmentId
      ? readKeptColumnsValue(earlierValue, columns)
      : Object.fromEntries(columns.map((column) => [column.index, defaultKeptColumnDraft(column)]));
  });
  const keptColumns = columns.filter(({ index }) => drafts[index]?.keep);
  const keptCount = keptColumns.length;
  const [isExpanded, setIsExpanded] = useState(keptCount > 0);

  const issues = useMemo(() => findKeptColumnsIssues(attachmentId, columns, drafts), [attachmentId, columns, drafts]);
  // Empty while the section can't be submitted, so the surface's `required` check blocks submit
  const submittedValue = issues.length ? '' : JSON.stringify(buildKeptColumnsValue(attachmentId, columns, drafts));

  useEffect(() => {
    if (submittedValue !== value) setValue(submittedValue);
    // Only the section's own changes write the value
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [submittedValue]);

  const updateDraft = (columnIndex: number, change: Partial<KeptColumnDraft>) =>
    setDrafts((current) => ({ ...current, [columnIndex]: { ...current[columnIndex], ...change } }));
  const issueMessage = (problem: string) => t(`aiInsights.a2ui.customFields.issues.${problem}`);
  const unlistedCount = keptCount - MAX_LISTED_KEPT_COLUMNS;

  return (
    <Section $expanded={isExpanded}>
      <SectionToggle type="button" aria-expanded={isExpanded} onClick={() => setIsExpanded(!isExpanded)}>
        <Chevron $expanded={isExpanded}>
          <ChevronIcon />
        </Chevron>
        <div>
          <SectionTitle>
            {t('aiInsights.a2ui.customFields.title')} <Muted>{t('aiInsights.a2ui.customFields.optional')}</Muted>
          </SectionTitle>
          {/* Collapsed, the kept columns read as on the card */}
          {!isExpanded && keptCount === 0 && <Muted>{t('aiInsights.a2ui.customFields.noneKept')}</Muted>}
          {!isExpanded && keptCount > 0 && (
            <KeptColumnsList>
              {keptColumns.slice(0, MAX_LISTED_KEPT_COLUMNS).map(({ index, columnName }) => (
                <KeptColumnLine
                  key={index}
                  columnName={columnName}
                  savedName={drafts[index].savedName}
                  fieldType={drafts[index].fieldType}
                />
              ))}
              {unlistedCount > 0 && (
                <Muted>{t('aiInsights.a2ui.customFields.moreColumns', { count: unlistedCount })}</Muted>
              )}
            </KeptColumnsList>
          )}
          {!isExpanded && issues.length > 0 && (
            <ErrorLine>{t('aiInsights.a2ui.customFields.collapsedIssues', { count: issues.length })}</ErrorLine>
          )}
        </div>
      </SectionToggle>
      {isExpanded && (
        <>
          <Description>{t('aiInsights.a2ui.customFields.description')}</Description>
          <GridRow>
            <span />
            <HeaderCell>{t('aiInsights.a2ui.customFields.column')}</HeaderCell>
            <HeaderCell>{t('aiInsights.a2ui.customFields.savedAs')}</HeaderCell>
            <HeaderCell>{t('aiInsights.a2ui.customFields.type')}</HeaderCell>
          </GridRow>
          {columns.map(({ index, columnName, suggestedFieldType }) => {
            const draft = drafts[index];
            const isSuggestedType =
              !!suggestedFieldType &&
              suggestedFieldType !== CustomFieldType.String &&
              draft.fieldType === suggestedFieldType;
            const rowIssue = issues.find((issue) => issue.columnIndex === index);
            return (
              <GridRow key={index} $highlighted={draft.keep} $invalid={!!rowIssue}>
                <Checkbox
                  aria-label={t('aiInsights.a2ui.customFields.keep', {
                    column: columnName,
                    interpolation: { escapeValue: false },
                  })}
                  isSelected={draft.keep}
                  onChange={(keep: boolean) => updateDraft(index, { keep })}
                />
                <ColumnCell>
                  {columnName}
                  {isSuggestedType && (
                    <SuggestedHint>
                      {t('aiInsights.a2ui.customFields.suggestedType', { fieldType: suggestedFieldType })}
                    </SuggestedHint>
                  )}
                </ColumnCell>
                <Input
                  fullWidth
                  marginBottom="0"
                  aria-label={t('aiInsights.a2ui.customFields.savedAs')}
                  disabled={!draft.keep}
                  value={draft.savedName}
                  onChange={(event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
                    updateDraft(index, { savedName: event.target.value })
                  }
                />
                <Dropdown
                  disabled={!draft.keep}
                  items={FIELD_TYPES.map((fieldType) => ({ key: fieldType, text: fieldType, value: fieldType }))}
                  selectedIndex={FIELD_TYPES.indexOf(draft.fieldType)}
                  onSelectionChange={(selection) => {
                    const selected = FIELD_TYPES[Number(Array.isArray(selection) ? selection[0] : selection)];
                    if (selected) updateDraft(index, { fieldType: selected });
                  }}
                />
                {rowIssue && (
                  <RowError>
                    <ErrorLine>{issueMessage(rowIssue.problem)}</ErrorLine>
                  </RowError>
                )}
              </GridRow>
            );
          })}
        </>
      )}
      {issues
        .filter((issue) => issue.columnIndex === undefined)
        .map((issue) => (
          <ErrorLine key={issue.problem}>{issueMessage(issue.problem)}</ErrorLine>
        ))}
    </Section>
  );
};

export default VisitiqCustomFields;
